import { useState, useCallback } from "react"
import { determineDeactivateMode } from "../../utils/determineDeactivateMode"
import { getMockDeactivationDetails } from "../../mocks/crewDeactivationMockData"

/**
 * Custom hook for managing crew deactivation/deletion modal state
 * @returns {Object} Modal states and handlers
 */
export const useCrewDeactivateModal = () => {
  const [deactivateDeleteModalState, setDeactivateDeleteModalState] = useState({
    visible: false,
    action: null, // 'deactivate' or 'delete'
    crew: null,
    mode: null, // Mode determined from API response
    details: null // Detailed data from API
  })
  const [loading, setLoading] = useState(false)

  const openDeactivateModal = useCallback((crew) => {
    setDeactivateDeleteModalState({
      visible: true,
      action: "deactivate",
      crew,
      mode: null,
      details: null
    })
    // Fetch deactivation details
    fetchDeactivationDetails(crew.id, "deactivate")
  }, [])

  const openDeleteModal = useCallback((crew) => {
    setDeactivateDeleteModalState({
      visible: true,
      action: "delete",
      crew,
      mode: null,
      details: null
    })
    // Fetch deletion details
    fetchDeactivationDetails(crew.id, "delete")
  }, [])

  const closeDeactivateDeleteModal = useCallback(() => {
    setDeactivateDeleteModalState({
      visible: false,
      action: null,
      crew: null,
      mode: null,
      details: null
    })
  }, [])

  const setDeactivateDeleteDetails = useCallback((details) => {
    const mode = determineDeactivateMode(details)
    setDeactivateDeleteModalState((prev) => ({
      ...prev,
      details,
      mode
    }))
  }, [])

  // Fetch deactivation/deletion details from API
  // Currently using mock data - replace with actual API call when available
  const fetchDeactivationDetails = useCallback(async (crewId, action) => {
    setLoading(true)
    try {
      // TODO: Replace with actual API call when available
      // const response = await dispatch(getCrewDeactivationDetails(crewId))
      // For now, use mock data based on crew ID
      const mockDetails = getMockDeactivationDetails(crewId)

      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500))

      const mode = determineDeactivateMode(mockDetails)
      setDeactivateDeleteModalState((prev) => ({
        ...prev,
        details: mockDetails,
        mode
      }))
    } catch (error) {
      console.error("Failed to fetch deactivation details:", error)
      closeDeactivateDeleteModal()
    } finally {
      setLoading(false)
    }
  }, [closeDeactivateDeleteModal])

  const handleConfirmDeactivateDelete = useCallback(async (data) => {
    setLoading(true)
    try {
      const { action, crew } = deactivateDeleteModalState
      // TODO: Replace with actual API call when available
      // if (action === 'deactivate') {
      //   await dispatch(deactivateCrew(crew.id, data))
      // } else {
      //   await dispatch(deleteCrew(crew.id, data))
      // }

      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500))

      console.log(`${action} crew:`, crew.id, "with data:", data)
      closeDeactivateDeleteModal()

      // Return success for the caller to handle (e.g., show toast, refresh data)
      return { success: true, action }
    } catch (error) {
      console.error("Failed to deactivate/delete crew:", error)
      return { success: false, error }
    } finally {
      setLoading(false)
    }
  }, [deactivateDeleteModalState, closeDeactivateDeleteModal])

  return {
    deactivateDeleteModalState,
    openDeactivateModal,
    openDeleteModal,
    closeDeactivateDeleteModal,
    setDeactivateDeleteDetails,
    handleConfirmDeactivateDelete,
    deactivateLoading: loading,
    deleteLoading: loading,
    deactivationDetailsLoading: loading,
    deletionDetailsLoading: loading
  }
}
